import React, { useEffect, useRef, useState } from 'react';
import { SectionRiveContent } from '@models/section';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { resolveImagePath } from '@helpers/gallery';
import { breakpoints } from '@web-for-marketing/react-ui';
import { RiveComponent } from './RiveComponent';
import { NoMobileDisplay } from './NoMobileDisplay';

const classes = {
    riveContainer: (aspectRatio: string) => ({
        width: '100%',
        height: 'auto',
        aspectRatio: aspectRatio,
    }),
    desktopOnly: {
        [`@media (max-width: ${breakpoints.md})`]: {
            display: 'none',
        },
    },
    mobileOnly: {
        margin: '0 auto',
        [`@media (min-width: ${breakpoints.md})`]: {
            display: 'none',
        },
    },
};

interface RiveContentProps {
    content: SectionRiveContent;
    lazyLoad?: boolean;
}

export function RiveContent({ content, lazyLoad }: RiveContentProps): React.ReactElement | null {
    const [isInView, setIsInView] = useState(!lazyLoad);
    const containerRef = useRef<HTMLDivElement>(null);
    const { mediaUrlPrefix } = useStaticValues();

    useEffect(() => {
        if (!lazyLoad) {
            return;
        }

        const riveContainer = containerRef.current;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    if (riveContainer) {
                        observer.unobserve(riveContainer);
                    }
                }
            },
            {
                rootMargin: '50px',
            }
        );

        if (riveContainer) {
            observer.observe(riveContainer);
        }

        return () => {
            if (lazyLoad && riveContainer) {
                observer.unobserve(riveContainer);
            }
        };
    }, [lazyLoad]);

    useEffect(() => {
        const riveContainer = containerRef.current;
        if (!riveContainer) {
            return;
        }

        /* need to update the height of the container when the rive canvas is resized to ensure the aspect ratio of the container matches the rive content
        thus preventing extra space from being added to the top and bottom of the container */
        const resizeObserver = new ResizeObserver(() => {
            if (content.riveAspectRatio) {
                const [w, h] = content.riveAspectRatio.split('/').map(Number);
                if (!isNaN(w) && !isNaN(h)) {
                    const width = riveContainer.offsetWidth;
                    if (width) {
                        riveContainer.style.height = `${(width * h) / w}px`;
                    }
                }
            }
        });

        resizeObserver.observe(riveContainer);

        return () => resizeObserver.disconnect();
    }, [content]);

    return content.src ? (
        <>
            {!content.enableMobile ? <NoMobileDisplay lazyLoad={lazyLoad} css={classes.mobileOnly} /> : null}
            <div
                key={JSON.stringify(content)}
                ref={containerRef}
                css={[
                    classes.riveContainer(`${content.riveAspectRatio}`),
                    !content.enableMobile ? classes.desktopOnly : undefined,
                ]}
            >
                {isInView ? <RiveComponent {...content} src={resolveImagePath(mediaUrlPrefix, content.src)} /> : null}
            </div>
        </>
    ) : null;
}
